import type { ValidatedToolResponse } from '@seen/core/models/validatedToolResponse'
import type { ReactNode } from 'react'
import { match } from 'ts-pattern'
import { AD8Form } from '#src/components/validated-tool-form/AD8Form.tsx'
import { ADLForm } from '#src/components/validated-tool-form/ADLForm.tsx'
import { BradenForm } from '#src/components/validated-tool-form/BradenForm.tsx'
import { COPMForm } from '#src/components/validated-tool-form/COPMForm.tsx'
import { ENNForm } from '#src/components/validated-tool-form/ENNForm.tsx'
import { FoodSecurityForm } from '#src/components/validated-tool-form/FoodSecurityForm.tsx'
import { GAD2Form } from '#src/components/validated-tool-form/GAD2Form.tsx'
import { GAD7Form } from '#src/components/validated-tool-form/GAD7Form.tsx'
import { HomeCareUtilizationForm } from '#src/components/validated-tool-form/HomeCareUtilizationForm.tsx'
import { IADLForm } from '#src/components/validated-tool-form/IADLForm.tsx'
import { MIIForm } from '#src/components/validated-tool-form/MIIForm.tsx'
import { MiniCogForm } from '#src/components/validated-tool-form/MiniCogForm.tsx'
import { MMSEForm } from '#src/components/validated-tool-form/MMSEForm.tsx'
import { MOCAForm } from '#src/components/validated-tool-form/MOCAForm.tsx'
import { PHQ2Form } from '#src/components/validated-tool-form/PHQ2Form.tsx'
import { PHQ9Form } from '#src/components/validated-tool-form/PHQ9Form.tsx'
import type { ValidatedToolInnerFormProps } from '#src/components/validated-tool-form/shared/ValidatedToolInnerForm.tsx'
import type {
  ValidatedToolFieldSuggestion,
  ValidatedToolSetValue,
} from '#src/components/validated-tool-form/shared/validatedToolSuggestion.ts'
import { SLUMSForm } from '#src/components/validated-tool-form/SLUMSForm.tsx'
import { STEADIForm } from '#src/components/validated-tool-form/STEADIForm.tsx'
import { TUGForm } from '#src/components/validated-tool-form/TUGForm.tsx'

export interface ValidatedToolFormProps extends Omit<
  ValidatedToolInnerFormProps,
  'validatedTool' | 'form' | 'subtitle'
> {
  name: string | undefined
  response?: ValidatedToolResponse
  /**
   * AI-staged answers to pre-populate an unsubmitted form. Every tool accepts
   * them; which tools the AI may actually propose for is decided server-side by
   * `PROPOSABLE_VALIDATED_TOOL_NAMES`, not by what this router can render.
   */
  prefillValuesByLinkId?: Record<string, string>
  /**
   * Build the per-field AI suggestion overlay (highlight plus the option's own
   * Accept/Reject/Undo controls), keyed by linkId, given the form's suggestion
   * `setValue` so a decision can seed or blank the field. A field the AI did not
   * answer gets no entry and renders exactly as it always did.
   *
   * The tool-HEADER controls (`renderSuggestionControls`) are deliberately NOT
   * named here: they carry no instrument knowledge, so they ride `...delegated`
   * through each form into `ValidatedToolInnerForm`, which every one of the
   * twenty already renders through.
   */
  renderFieldSuggestions?: (
    setValue: ValidatedToolSetValue
  ) => Record<string, ValidatedToolFieldSuggestion>
  restorationId?: string
  isLoading?: boolean
  disabled?: boolean
}

export function ValidatedToolForm({
  name,
  response,
  prefillValuesByLinkId,
  renderFieldSuggestions,
  isLoading,
  disabled,
  restorationId,
  ...delegated
}: ValidatedToolFormProps): ReactNode {
  if (!name || isLoading) {
    return null
  }
  return match(name)
    .with('ad8', () => (
      <AD8Form
        response={response}
        prefillValuesByLinkId={prefillValuesByLinkId}
        renderFieldSuggestions={renderFieldSuggestions}
        restorationId={restorationId}
        {...delegated}
      />
    ))
    .with('adl', () => (
      <ADLForm
        response={response}
        prefillValuesByLinkId={prefillValuesByLinkId}
        renderFieldSuggestions={renderFieldSuggestions}
        restorationId={restorationId}
        {...delegated}
      />
    ))
    .with('braden', () => (
      <BradenForm
        response={response}
        prefillValuesByLinkId={prefillValuesByLinkId}
        renderFieldSuggestions={renderFieldSuggestions}
        restorationId={restorationId}
        {...delegated}
      />
    ))
    .with('enn', () => (
      <ENNForm
        response={response}
        prefillValuesByLinkId={prefillValuesByLinkId}
        renderFieldSuggestions={renderFieldSuggestions}
        restorationId={restorationId}
        disabled={disabled}
        {...delegated}
      />
    ))
    .with('food security', () => (
      <FoodSecurityForm
        response={response}
        prefillValuesByLinkId={prefillValuesByLinkId}
        renderFieldSuggestions={renderFieldSuggestions}
        restorationId={restorationId}
        {...delegated}
      />
    ))
    .with('gad-2', () => (
      <GAD2Form
        response={response}
        prefillValuesByLinkId={prefillValuesByLinkId}
        renderFieldSuggestions={renderFieldSuggestions}
        restorationId={restorationId}
        {...delegated}
      />
    ))
    .with('gad-7', () => (
      <GAD7Form
        response={response}
        prefillValuesByLinkId={prefillValuesByLinkId}
        renderFieldSuggestions={renderFieldSuggestions}
        restorationId={restorationId}
        {...delegated}
      />
    ))
    .with('iadl', () => (
      <IADLForm
        response={response}
        prefillValuesByLinkId={prefillValuesByLinkId}
        renderFieldSuggestions={renderFieldSuggestions}
        restorationId={restorationId}
        {...delegated}
      />
    ))
    .with('home care utilization', () => (
      <HomeCareUtilizationForm
        response={response}
        prefillValuesByLinkId={prefillValuesByLinkId}
        renderFieldSuggestions={renderFieldSuggestions}
        restorationId={restorationId}
        {...delegated}
      />
    ))
    .with('mini-cog', () => (
      <MiniCogForm
        response={response}
        prefillValuesByLinkId={prefillValuesByLinkId}
        renderFieldSuggestions={renderFieldSuggestions}
        restorationId={restorationId}
        {...delegated}
      />
    ))
    .with('mii', () => (
      <MIIForm
        response={response}
        prefillValuesByLinkId={prefillValuesByLinkId}
        renderFieldSuggestions={renderFieldSuggestions}
        restorationId={restorationId}
        {...delegated}
      />
    ))
    .with('mmse', () => (
      <MMSEForm
        response={response}
        prefillValuesByLinkId={prefillValuesByLinkId}
        renderFieldSuggestions={renderFieldSuggestions}
        restorationId={restorationId}
        {...delegated}
      />
    ))
    .with('moca', () => (
      <MOCAForm
        response={response}
        prefillValuesByLinkId={prefillValuesByLinkId}
        renderFieldSuggestions={renderFieldSuggestions}
        restorationId={restorationId}
        {...delegated}
      />
    ))
    .with('phq-2', () => (
      <PHQ2Form
        response={response}
        prefillValuesByLinkId={prefillValuesByLinkId}
        renderFieldSuggestions={renderFieldSuggestions}
        restorationId={restorationId}
        {...delegated}
      />
    ))
    .with('phq-9', () => (
      <PHQ9Form
        response={response}
        prefillValuesByLinkId={prefillValuesByLinkId}
        renderFieldSuggestions={renderFieldSuggestions}
        restorationId={restorationId}
        {...delegated}
      />
    ))
    .with('slums', () => (
      <SLUMSForm
        response={response}
        prefillValuesByLinkId={prefillValuesByLinkId}
        renderFieldSuggestions={renderFieldSuggestions}
        restorationId={restorationId}
        {...delegated}
      />
    ))
    .with('steadi', () => (
      <STEADIForm
        response={response}
        prefillValuesByLinkId={prefillValuesByLinkId}
        renderFieldSuggestions={renderFieldSuggestions}
        restorationId={restorationId}
        {...delegated}
      />
    ))
    .with('tug', () => (
      <TUGForm
        response={response}
        prefillValuesByLinkId={prefillValuesByLinkId}
        renderFieldSuggestions={renderFieldSuggestions}
        restorationId={restorationId}
        {...delegated}
      />
    ))
    .with('copm', () => (
      <COPMForm
        response={response}
        prefillValuesByLinkId={prefillValuesByLinkId}
        renderFieldSuggestions={renderFieldSuggestions}
        restorationId={restorationId}
        {...delegated}
      />
    ))
    .otherwise(() => {
      throw new Error(`Invalid validated tool name: ${name}`)
    })
}
